/**
 * StorageService - Clean database persistence
 * Production Supabase-only persistence
 * Zero localStorage dependency
 */
import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'
import type { BucketConfig } from '../types/bucket'
import { logInfo, logError, logWarn } from '../utils/logger'
import { shouldBypassSupabaseRequests } from '../lib/runtimeConfig'
import { CANVAS_COLORS } from '../utils/tokens/canvas'

type _VisionBoard = Database['public']['Tables']['vision_boards']['Row']

const LOCAL_BOARDS_KEY = 'printboard_dev_boards_v1'
const LOCAL_CHAT_SESSIONS_KEY = 'printboard_dev_chat_sessions_v1'

// Chat message type for chat sessions
export interface ChatMessage {
  id?: string // Optional for backward compatibility with existing database records
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp?: string
  [key: string]: unknown
}

export interface BoardData {
  id?: string
  title: string
  description?: string
  templateType?: string
  canvasState: string
  bucketConfig?: BucketConfig[]
  userId: string
  isPublic?: boolean
  thumbnail?: string | null
  createdAt?: string
  updatedAt?: string
  lastSaved?: string
}

interface ThumbnailCanvasState {
  elements?: Array<Record<string, unknown>>
  width?: number
  height?: number
  cornerStyle?: string
  spacingStyle?: string
}

const THUMBNAIL_INSET_MAP = {
  fitted: 0,
  light: 4,
  generous: 8,
} as const

const escapeSvgText = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const encodeSvgDataUrl = (svg: string): string => {
  const utf8Svg = unescape(encodeURIComponent(svg))
  if (typeof btoa === 'function') {
    return `data:image/svg+xml;base64,${btoa(utf8Svg)}`
  }

  const bufferCtor = (globalThis as { Buffer?: typeof Buffer }).Buffer
  if (bufferCtor) {
    return `data:image/svg+xml;base64,${bufferCtor.from(svg, 'utf-8').toString('base64')}`
  }

  throw new Error('No base64 encoder available for thumbnail generation')
}

export class StorageService {
  private parseThumbnailCanvasState(
    canvasState: string
  ): ThumbnailCanvasState | null {
    try {
      const parsed = JSON.parse(canvasState)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null
      }

      return parsed as ThumbnailCanvasState
    } catch {
      return null
    }
  }

  private getElementFrame(
    element: Record<string, unknown>
  ): { x: number; y: number; width: number; height: number } | null {
    const rawX =
      typeof element.x === 'number'
        ? element.x
        : typeof element.left === 'number'
          ? element.left
          : null
    const rawY =
      typeof element.y === 'number'
        ? element.y
        : typeof element.top === 'number'
          ? element.top
          : null
    if (rawX === null || rawY === null) return null

    const scaleX =
      typeof element.scaleX === 'number' && Number.isFinite(element.scaleX)
        ? Math.max(0.01, element.scaleX)
        : 1
    const scaleY =
      typeof element.scaleY === 'number' && Number.isFinite(element.scaleY)
        ? Math.max(0.01, element.scaleY)
        : 1
    const baseWidth =
      typeof element.width === 'number' && Number.isFinite(element.width)
        ? element.width
        : 0
    const baseHeight =
      typeof element.height === 'number' && Number.isFinite(element.height)
        ? element.height
        : 0
    const width = Math.max(24, baseWidth * scaleX)
    const height = Math.max(24, baseHeight * scaleY)

    return { x: rawX, y: rawY, width, height }
  }

  private getThumbnailCornerRadius(
    cornerStyle: string | undefined,
    width: number,
    height: number
  ): number {
    if (cornerStyle === 'none') return 0

    const minDimension = Math.max(1, Math.min(width, height))
    if (cornerStyle === 'rounded') {
      return Math.max(12, minDimension * 0.11)
    }

    return Math.max(6, minDimension * 0.05)
  }

  private canUseLocalStorage(): boolean {
    return typeof window !== 'undefined' && !!window.localStorage
  }

  private readLocalBoards(): BoardData[] {
    if (!this.canUseLocalStorage()) return []

    const raw = window.localStorage.getItem(LOCAL_BOARDS_KEY)
    if (!raw) return []

    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? (parsed as BoardData[]) : []
    } catch {
      return []
    }
  }

  private writeLocalBoards(boards: BoardData[]): void {
    if (!this.canUseLocalStorage()) return
    window.localStorage.setItem(LOCAL_BOARDS_KEY, JSON.stringify(boards))
  }

  private getLocalChatSessionMap(): Record<string, ChatMessage[]> {
    if (!this.canUseLocalStorage()) return {}

    const raw = window.localStorage.getItem(LOCAL_CHAT_SESSIONS_KEY)
    if (!raw) return {}

    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object'
        ? (parsed as Record<string, ChatMessage[]>)
        : {}
    } catch {
      return {}
    }
  }

  private setLocalChatSessionMap(map: Record<string, ChatMessage[]>): void {
    if (!this.canUseLocalStorage()) return
    window.localStorage.setItem(LOCAL_CHAT_SESSIONS_KEY, JSON.stringify(map))
  }

  private getLocalChatSessionKey(userId: string, boardId: string): string {
    return `${userId}:${boardId}`
  }

  private hasMessage(error: unknown): error is { message: string } {
    return (
      !!error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof (error as { message?: unknown }).message === 'string'
    )
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error) return error.message
    if (this.hasMessage(error)) return error.message
    return fallback
  }

  async saveBoard(boardData: BoardData): Promise<{
    success: boolean
    boardId?: string
    lastSaved?: string
    error?: string
  }> {
    try {
      const now = new Date().toISOString()

      if (shouldBypassSupabaseRequests) {
        const boards = this.readLocalBoards()
        const boardId = boardData.id || crypto.randomUUID()
        const existingIndex = boards.findIndex((board) => board.id === boardId)
        const existingBoard = existingIndex >= 0 ? boards[existingIndex] : null
        const nextThumbnail =
          boardData.thumbnail !== undefined
            ? boardData.thumbnail
            : existingBoard?.thumbnail

        const nextBoard: BoardData = {
          ...existingBoard,
          ...boardData,
          id: boardId,
          userId: boardData.userId,
          thumbnail: nextThumbnail ?? undefined,
          createdAt: existingBoard?.createdAt || boardData.createdAt || now,
          updatedAt: now,
          lastSaved: now,
        }

        if (existingIndex >= 0) {
          boards[existingIndex] = nextBoard
        } else {
          boards.unshift(nextBoard)
        }

        this.writeLocalBoards(boards)
        return { success: true, boardId, lastSaved: now }
      }

      // Parse canvas_data if it's a string, add bucketConfig
      let canvasData: Record<string, unknown>
      try {
        canvasData =
          typeof boardData.canvasState === 'string'
            ? JSON.parse(boardData.canvasState)
            : boardData.canvasState
      } catch {
        canvasData = {}
      }

      // Validate canvasData is a proper object
      if (
        typeof canvasData !== 'object' ||
        canvasData === null ||
        Array.isArray(canvasData)
      ) {
        logError(
          'StorageService',
          'Invalid canvas data format, using empty object',
          {
            type: typeof canvasData,
            isNull: canvasData === null,
            isArray: Array.isArray(canvasData),
          }
        )
        canvasData = {}
      }

      // Include bucket config in canvas data
      if (boardData.bucketConfig) {
        canvasData.bucketConfig = boardData.bucketConfig
      }

      const inferredTemplateType =
        boardData.templateType ||
        (canvasData.template === 'bucket8' || canvasData.template === 'blank'
          ? canvasData.template
          : 'blank')

      // Build upsert data - only include id if it exists
      const upsertData: Record<string, unknown> = {
        title: boardData.title,
        description: boardData.description,
        template_type: inferredTemplateType,
        canvas_data: canvasData,
        user_id: boardData.userId,
        is_public: boardData.isPublic || false,
        thumbnail_url: boardData.thumbnail ?? null,
        last_saved_at: now,
        updated_at: now,
      }

      // Only include id if it exists (for updates, not inserts)
      if (boardData.id) {
        upsertData.id = boardData.id
      }

      logInfo('StorageService', 'Saving board', {
        hasId: !!boardData.id,
        boardId: boardData.id,
        canvasDataKeys: Object.keys(canvasData).slice(0, 10), // First 10 keys
        canvasDataSize: JSON.stringify(canvasData).length,
        userId: boardData.userId,
        title: boardData.title,
      })

      try {
        const data = await this.retryOperation('saveBoard', async () => {
          const { data, error } = await supabase
            .from('vision_boards')
            .upsert(upsertData)
            .select('id')
            .single()
          if (error) throw error
          return data
        })

        return { success: true, boardId: data?.id, lastSaved: now }
      } catch (error) {
        return {
          success: false,
          error: this.getErrorMessage(error, 'Unknown error saving board'),
        }
      }
    } catch (error) {
      return {
        success: false,
        error: this.getErrorMessage(error, 'Unknown error saving board'),
      }
    }
  }

  async loadBoard(
    boardId: string
  ): Promise<{ success: boolean; board?: BoardData; error?: string }> {
    try {
      if (shouldBypassSupabaseRequests) {
        const board = this.readLocalBoards().find((item) => item.id === boardId)
        if (!board) {
          return { success: false, error: 'Board not found' }
        }

        return { success: true, board }
      }

      const data = await this.retryOperation('loadBoard', async () => {
        const { data, error } = await supabase
          .from('vision_boards')
          .select('*')
          .eq('id', boardId)
          .maybeSingle()
        if (error) throw error
        if (!data) throw new Error('Board not found')
        return data
      })

      // Extract canvas data and bucket config
      let canvasData: Record<string, unknown>
      let bucketConfig: BucketConfig[] | undefined

      try {
        canvasData =
          typeof data.canvas_data === 'string'
            ? JSON.parse(data.canvas_data)
            : data.canvas_data || {}

        // Extract bucket config from canvas data if present
        if (canvasData.bucketConfig && Array.isArray(canvasData.bucketConfig)) {
          bucketConfig = canvasData.bucketConfig
          // Remove from canvas data to keep it clean
          delete canvasData.bucketConfig
        }
      } catch {
        canvasData = {}
      }

      const board: BoardData = {
        id: data.id,
        title: data.title,
        description: data.description || undefined,
        templateType:
          (typeof data.template_type === 'string' &&
          (data.template_type === 'bucket8' || data.template_type === 'blank')
            ? data.template_type
            : undefined) ||
          ((canvasData.template === 'bucket8' || canvasData.template === 'blank'
            ? canvasData.template
            : undefined) as 'bucket8' | 'blank' | undefined) ||
          (Array.isArray(bucketConfig) && bucketConfig.length > 0
            ? 'bucket8'
            : 'blank'),
        canvasState: JSON.stringify(canvasData),
        bucketConfig: bucketConfig,
        userId: data.user_id,
        isPublic: data.is_public || false,
        thumbnail: data.thumbnail_url || undefined,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        lastSaved: data.last_saved_at || undefined,
      }

      return { success: true, board }
    } catch (error) {
      return {
        success: false,
        error: this.getErrorMessage(error, 'Unknown error loading board'),
      }
    }
  }

  async updateBoardThumbnail(
    boardId: string,
    thumbnailUrl: string | null
  ): Promise<{ success: boolean; error?: string }> {
    try {
      if (shouldBypassSupabaseRequests) {
        const boards = this.readLocalBoards()
        const existingIndex = boards.findIndex((board) => board.id === boardId)
        if (existingIndex < 0) {
          return { success: false, error: 'Board not found' }
        }

        boards[existingIndex] = {
          ...boards[existingIndex],
          thumbnail: thumbnailUrl ?? undefined,
        }
        this.writeLocalBoards(boards)
        return { success: true }
      }

      const { error } = await supabase
        .from('vision_boards')
        .update({ thumbnail_url: thumbnailUrl })
        .eq('id', boardId)

      if (error) throw error

      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: this.getErrorMessage(error, 'Unknown error updating thumbnail'),
      }
    }
  }

  async listUserBoards(
    userId: string
  ): Promise<{ success: boolean; boards?: BoardData[]; error?: string }> {
    try {
      if (shouldBypassSupabaseRequests) {
        const boards = this.readLocalBoards()
          .filter((board) => board.userId === userId)
          .sort((a, b) => {
            const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime()
            const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime()
            return bTime - aTime
          })
        return { success: true, boards }
      }

      const data = await this.retryOperation('listUserBoards', async () => {
        const { data, error } = await supabase
          .from('vision_boards')
          .select(
            'id, title, description, thumbnail_url, created_at, updated_at, is_public'
          )
          .eq('user_id', userId)
          .order('updated_at', { ascending: false })
        if (error) throw error
        return data
      })

      const boards: BoardData[] = data.map((row) => ({
        id: row.id,
        title: row.title,
        description: row.description || undefined,
        canvasState: '{}', // Don't load full canvas state for lists
        userId: userId,
        isPublic: row.is_public || false,
        thumbnail: row.thumbnail_url || undefined,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }))

      return { success: true, boards }
    } catch (error) {
      return {
        success: false,
        error: this.getErrorMessage(error, 'Unknown error'),
      }
    }
  }

  async deleteBoard(
    boardId: string,
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      if (shouldBypassSupabaseRequests) {
        const remainingBoards = this.readLocalBoards().filter(
          (board) => !(board.id === boardId && board.userId === userId)
        )
        this.writeLocalBoards(remainingBoards)

        const chatMap = this.getLocalChatSessionMap()
        delete chatMap[this.getLocalChatSessionKey(userId, boardId)]
        this.setLocalChatSessionMap(chatMap)

        return { success: true }
      }

      await this.retryOperation('deleteBoard', async () => {
        const { error } = await supabase
          .from('vision_boards')
          .delete()
          .eq('id', boardId)
          .eq('user_id', userId)
        if (error) throw error
      })

      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: this.getErrorMessage(error, 'Unknown error'),
      }
    }
  }

  // Generate a lightweight board-state-derived thumbnail when a rendered image is unavailable.
  generateThumbnail(canvasState: string): string {
    const parsed = this.parseThumbnailCanvasState(canvasState)
    const width =
      parsed?.width && Number.isFinite(parsed.width) && parsed.width > 0
        ? parsed.width
        : 1200
    const height =
      parsed?.height && Number.isFinite(parsed.height) && parsed.height > 0
        ? parsed.height
        : 800
    const thumbWidth = 320
    const thumbHeight = Math.max(160, Math.round((height / width) * thumbWidth))
    const spacingInset =
      THUMBNAIL_INSET_MAP[
        (parsed?.spacingStyle as keyof typeof THUMBNAIL_INSET_MAP) || 'light'
      ] ?? THUMBNAIL_INSET_MAP.light
    const defs: string[] = []
    const layers: string[] = [
      `<rect width="${width}" height="${height}" fill="${CANVAS_COLORS.background.board}" />`,
    ]
    const elements = Array.isArray(parsed?.elements) ? parsed!.elements : []

    elements.forEach((element, index) => {
      const type = element.type
      if (type !== 'image' && type !== 'text') return

      const frame = this.getElementFrame(element)
      if (!frame) return

      if (type === 'image') {
        const inset = Math.min(
          spacingInset,
          Math.max(0, Math.min(frame.width, frame.height) / 8)
        )
        const x = frame.x + inset
        const y = frame.y + inset
        const imageWidth = Math.max(4, frame.width - inset * 2)
        const imageHeight = Math.max(4, frame.height - inset * 2)
        const radius = this.getThumbnailCornerRadius(
          parsed?.cornerStyle,
          imageWidth,
          imageHeight
        )
        const clipId = `thumb-clip-${index}`

        defs.push(
          `<clipPath id="${clipId}"><rect x="${x}" y="${y}" width="${imageWidth}" height="${imageHeight}" rx="${radius}" ry="${radius}" /></clipPath>`
        )
        layers.push(
          `<rect x="${x}" y="${y}" width="${imageWidth}" height="${imageHeight}" rx="${radius}" ry="${radius}" fill="#E7E0D2" />`
        )

        const rawSrc = typeof element.src === 'string' ? element.src.trim() : ''
        if (rawSrc) {
          layers.push(
            `<image href="${escapeSvgText(rawSrc)}" x="${x}" y="${y}" width="${imageWidth}" height="${imageHeight}" preserveAspectRatio="xMidYMid slice" clip-path="url(#${clipId})" />`
          )
        }

        layers.push(
          `<rect x="${x}" y="${y}" width="${imageWidth}" height="${imageHeight}" rx="${radius}" ry="${radius}" fill="none" stroke="rgba(61,59,54,0.08)" stroke-width="2" />`
        )
        return
      }

      const text = typeof element.text === 'string' ? element.text.trim() : ''
      if (!text) return

      const fontSize =
        typeof element.fontSize === 'number' &&
        Number.isFinite(element.fontSize)
          ? element.fontSize
          : 28
      layers.push(
        `<text x="${frame.x}" y="${frame.y + fontSize}" font-family="Georgia, serif" font-size="${fontSize}" fill="#3D3B36">${escapeSvgText(
          text.slice(0, 80)
        )}</text>`
      )
    })

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${thumbWidth}" height="${thumbHeight}" viewBox="0 0 ${width} ${height}" fill="none">${defs.length > 0 ? `<defs>${defs.join('')}</defs>` : ''}${layers.join('')}</svg>`

    return encodeSvgDataUrl(svg)
  }

  /**
   * Save chat session messages to database
   * Uses upsert to create or update existing chat session
   */
  async saveChatSession(
    userId: string,
    boardId: string,
    messages: ChatMessage[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const now = new Date().toISOString()

      if (shouldBypassSupabaseRequests) {
        const sessionMap = this.getLocalChatSessionMap()
        sessionMap[this.getLocalChatSessionKey(userId, boardId)] = messages
        this.setLocalChatSessionMap(sessionMap)
        return { success: true }
      }

      logInfo('StorageService', 'Saving chat session', {
        userId,
        boardId,
        messageCount: messages.length,
        timestamp: now,
      })

      const data = await this.retryOperation('saveChatSession', async () => {
        const { data, error } = await supabase
          .from('chat_sessions')
          .upsert(
            {
              user_id: userId,
              vision_board_id: boardId,
              messages: messages,
              updated_at: now,
            },
            {
              onConflict: 'user_id,vision_board_id',
            }
          )
          .select()
        if (error) throw error
        return data
      })

      logInfo('StorageService', 'Chat session saved successfully', {
        sessionCount: data?.length || 0,
      })

      return { success: true }
    } catch (error) {
      logError('StorageService', 'Chat session save exception', { error })
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Unknown error saving chat session',
      }
    }
  }

  /**
   * Load chat session messages from database
   * Returns empty array if no session exists
   */
  async loadChatSession(
    userId: string,
    boardId: string
  ): Promise<{ success: boolean; messages?: ChatMessage[]; error?: string }> {
    try {
      if (shouldBypassSupabaseRequests) {
        const sessionMap = this.getLocalChatSessionMap()
        const key = this.getLocalChatSessionKey(userId, boardId)
        return { success: true, messages: sessionMap[key] || [] }
      }

      logInfo('StorageService', 'Loading chat session', {
        userId,
        boardId,
      })

      const data = await this.retryOperation('loadChatSession', async () => {
        const { data, error } = await supabase
          .from('chat_sessions')
          .select('messages, id, updated_at, last_saved_at')
          .eq('user_id', userId)
          .eq('vision_board_id', boardId)
          .maybeSingle()
        if (error) throw error
        return data
      })

      // Return empty array if no session exists
      const messages = data?.messages || []

      logInfo('StorageService', 'Chat session loaded', {
        found: !!data,
        sessionId: data?.id,
        messageCount: messages.length,
        lastSaved: data?.last_saved_at,
        lastUpdated: data?.updated_at,
      })

      return { success: true, messages }
    } catch (error) {
      logError('StorageService', 'Chat session load exception', { error })
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Unknown error loading chat session',
      }
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (!error) return 'Unknown error'
    if (typeof error === 'string') return error
    if (error instanceof Error) return error.message
    if (
      typeof error === 'object' &&
      'message' in (error as Record<string, unknown>)
    ) {
      const maybeMessage = (error as Record<string, unknown>).message
      if (typeof maybeMessage === 'string') return maybeMessage
    }
    try {
      return JSON.stringify(error)
    } catch {
      return String(error)
    }
  }

  private isTransientError(message: string): boolean {
    const normalized = message.toLowerCase()
    if (normalized.includes('content security policy')) {
      return false
    }

    return (
      normalized.includes('err_connection_closed') ||
      normalized.includes('err_connection_reset') ||
      normalized.includes('connection reset') ||
      normalized.includes('failed to fetch') ||
      normalized.includes('network request failed')
    )
  }

  private async retryOperation<T>(
    label: string,
    operation: () => Promise<T>,
    retries = 2
  ): Promise<T> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await operation()
      } catch (error) {
        const message = this.extractErrorMessage(error)
        const isTransient = this.isTransientError(message)
        if (!isTransient) {
          throw error instanceof Error ? error : new Error(message)
        }

        if (attempt === retries) {
          logError('StorageService', `${label} failed after retries`, {
            error: message,
          })
          throw new Error('We lost connection for a moment. Please try again.')
        }

        logWarn('StorageService', `${label} transient network issue`, {
          attempt: attempt + 1,
          retries,
          error: message,
        })
        await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)))
      }
    }

    throw new Error(`${label} failed`)
  }
}
